import { type ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Folha modal (diálogo) com animação de entrada/saída.
 * Fecha com Esc ou clique fora; devolve o foco ao elemento anterior.
 */
export function Sheet({ children, onClose, wide, label }: { children: ReactNode; onClose: () => void; wide?: boolean; label?: string }) {
  const [open, setOpen] = useState(false);
  const prevFocus = useRef<Element | null>(null);
  useEffect(() => {
    prevFocus.current = document.activeElement;
    const r = requestAnimationFrame(() => requestAnimationFrame(() => setOpen(true)));
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !document.querySelector('.picker')) { e.stopPropagation(); onClose(); } };
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(r);
      document.removeEventListener('keydown', onKey);
      (prevFocus.current as HTMLElement | null)?.focus?.();
    };
  }, [onClose]);
  return createPortal(
    <div className={`layer show ${open ? 'open' : ''}`}>
      <div className="dim" onClick={onClose} />
      <div className={`sheet ${wide ? 'wide' : ''}`} role="dialog" aria-modal="true" aria-label={label}>{children}</div>
    </div>,
    document.body,
  );
}

export function SheetFooter({ children }: { children: ReactNode }) {
  return <div className="sh-foot">{children}</div>;
}

/** Foca (e opcionalmente seleciona) um campo assim que a folha abre. */
export function useAutoFocus<T extends HTMLInputElement | HTMLTextAreaElement | HTMLButtonElement>(select = false) {
  const ref = useRef<T>(null);
  useEffect(() => {
    const t = setTimeout(() => { ref.current?.focus(); if (select && 'select' in (ref.current ?? {})) (ref.current as HTMLInputElement).select(); }, 60);
    return () => clearTimeout(t);
  }, [select]);
  return ref;
}
